import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../hooks/use-toast";
import ImageUpload from "../components/ImageUpload";
import EnquiryThread, { StatusBadge } from "../components/EnquiryThread";
import { Loader2, User, Inbox, PoundSterling, Search, LogOut, Music, MapPin, Building2, X, CalendarDays, ExternalLink } from "lucide-react";

const TABS = [
  { id: "profile", label: "My profile", icon: User },
  { id: "gigs", label: "My gigs", icon: CalendarDays },
  { id: "inbox", label: "Enquiries", icon: Inbox },
  { id: "bookings", label: "Bookings & pay", icon: PoundSterling },
  { id: "find", label: "Find & enquire", icon: Search },
];

const dealLabel = (d) => {
  if (!d) return "—";
  if (d.deal_type === "percent") return `${d.value}% of sales${d.min_fee ? `, min £${d.min_fee}` : ""}`;
  if (d.deal_type === "per_ticket") return `£${d.value}/ticket${d.min_fee ? `, min £${d.min_fee}` : ""}`;
  return `£${d.value} flat`;
};

export default function ArtistDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (!loading && (!user || user.role !== "artist")) nav("/login");
  }, [user, loading, nav]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={36} /></div>;

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <header className="bg-[#16243b] text-white">
        <div className="max-w-[1000px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Music size={20} className="text-[#2ec9c9]" /><span className="font-bold">TicketsPro <span className="text-[#2ec9c9]">Artists</span></span></div>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="artist-logout" className="text-sm text-white/80 hover:text-white flex items-center gap-1.5"><LogOut size={15} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-6 py-6">
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`artist-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-[#2ec9c9] text-white" : "bg-white text-[#5a6b80] hover:text-[#16243b]"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "profile" && <ProfileTab />}
        {tab === "gigs" && <GigsTab />}
        {tab === "inbox" && <InboxTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "find" && <FindTab />}
      </div>
    </div>
  );
}

function ProfileTab() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/artist/me").then(({ data }) => setP({ ...data, genresStr: (data.genres || []).join(", ") })); }, []);
  if (!p) return <Loader2 className="animate-spin text-[#2ec9c9]" size={28} />;
  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: p.name, bio: p.bio, image_url: p.image_url, city: p.city, base_fee: p.base_fee === "" || p.base_fee == null ? null : Number(p.base_fee), fee_note: p.fee_note, website: p.website, instagram: p.instagram, contact_email: p.contact_email, genres: (p.genresStr || "").split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.put("/artist/me", payload);
      setP({ ...data, genresStr: (data.genres || []).join(", ") });
      toast({ title: "Profile saved" });
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm";
  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[640px]" data-testid="artist-profile-form">
      {p.slug && (
        <a href={`/artists/${p.slug}`} target="_blank" rel="noreferrer" data-testid="artist-public-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#1fb0b0] font-medium hover:underline mb-4">
          View my public page ↗
        </a>
      )}
      <div className="grid gap-4">
        <div className="flex gap-4 items-start">
          <div className="w-28 shrink-0"><ImageUpload label="Photo" value={p.image_url} onChange={(v) => set("image_url", v)} /></div>
          <div className="flex-1 grid gap-4">
            <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Artist / act name</label>
              <input value={p.name ?? ""} onChange={(e) => set("name", e.target.value)} placeholder="DJ Aurora" data-testid="artist-profile-name" className={inputCls} /></div>
            <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">City / based in</label>
              <input value={p.city ?? ""} onChange={(e) => set("city", e.target.value)} placeholder="Glasgow" data-testid="artist-profile-city" className={inputCls} /></div>
          </div>
        </div>
        <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Bio</label>
          <textarea value={p.bio ?? ""} onChange={(e) => set("bio", e.target.value)} placeholder="Tell venues about your act…" rows={4} data-testid="artist-profile-bio" className={inputCls} /></div>
        <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Genres (comma-separated)</label>
          <input value={p.genresStr ?? ""} onChange={(e) => set("genresStr", e.target.value)} placeholder="House, Techno, Disco" data-testid="artist-profile-genres" className={inputCls} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Expected fee (£)</label>
            <input type="number" value={p.base_fee ?? ""} onChange={(e) => set("base_fee", e.target.value)} placeholder="250" data-testid="artist-profile-base_fee" className={inputCls} /></div>
          <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Fee note</label>
            <input value={p.fee_note ?? ""} onChange={(e) => set("fee_note", e.target.value)} placeholder="Negotiable / incl. travel" data-testid="artist-profile-fee_note" className={inputCls} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Website</label>
            <input value={p.website ?? ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" data-testid="artist-profile-website" className={inputCls} /></div>
          <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Instagram</label>
            <input value={p.instagram ?? ""} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" data-testid="artist-profile-instagram" className={inputCls} /></div>
        </div>
        <div><label className="block text-sm font-medium text-[#16243b] mb-1.5">Contact email</label>
          <input type="email" value={p.contact_email ?? ""} onChange={(e) => set("contact_email", e.target.value)} placeholder="you@example.com" data-testid="artist-profile-contact_email" className={inputCls} /></div>
        <button onClick={save} disabled={busy} data-testid="artist-profile-save" className="gp-btn-teal rounded-xl py-3 font-medium disabled:opacity-60">{busy ? "Saving…" : "Save profile"}</button>
      </div>
    </div>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }
function icsStamp(d) { return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`; }
function downloadGigIcs(g) {
  const start = new Date(g.date);
  if (g.set_time && g.set_time.includes(":")) { const [h, m] = g.set_time.split(":"); start.setHours(+h, +m); }
  const end = new Date(start.getTime() + 2 * 3600 * 1000);
  const esc = (s) => (s || "").replace(/\n/g, " ").replace(/,/g, "\\,");
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//TicketsPro//Gigs//EN", "BEGIN:VEVENT",
    `UID:${g.event_id}@ticketspro.app`, `DTSTAMP:${icsStamp(new Date())}`, `DTSTART:${icsStamp(start)}`, `DTEND:${icsStamp(end)}`,
    `SUMMARY:${esc(g.title)}`, `LOCATION:${esc(g.venue || g.with)}`, `DESCRIPTION:${esc("With " + (g.with || ""))}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${(g.title || "gig").toLowerCase().replace(/\s+/g, "-")}.ics`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

function GigsTab() {
  const [gigs, setGigs] = useState(null);
  const [feed, setFeed] = useState(null);
  useEffect(() => { api.get("/artist/gigs").then(({ data }) => setGigs(data)); }, []);
  const subscribe = async () => {
    try {
      const { data } = await api.get("/artist/ics");
      setFeed(data);
      try { await navigator.clipboard.writeText(data.feed_url); toast({ title: "Calendar link copied", description: "Paste it into Google/Apple Calendar → Add by URL to subscribe." }); }
      catch { toast({ title: "Calendar link ready", description: "Copy the link below and add it in Google/Apple Calendar → Add by URL." }); }
    } catch { toast({ title: "Could not create calendar link", variant: "destructive" }); }
  };
  if (!gigs) return <Loader2 className="animate-spin text-[#2ec9c9]" size={28} />;
  if (gigs.length === 0) return <div className="bg-white rounded-2xl p-8 text-center text-[#8a97a8]" data-testid="gigs-empty">No upcoming gigs yet. Once an organiser adds you to an event lineup or books an enquiry, it'll appear here.</div>;
  const groups = {};
  gigs.forEach((g) => {
    const d = new Date(g.date);
    const key = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
    (groups[key] = groups[key] || []).push(g);
  });
  return (
    <div className="space-y-6" data-testid="gigs-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#5a6b80]">{gigs.length} upcoming gig{gigs.length === 1 ? "" : "s"}</p>
        <button onClick={subscribe} data-testid="gigs-subscribe" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1.5"><CalendarDays size={15} /> Subscribe (Google/Apple)</button>
      </div>
      {feed && (
        <div className="rounded-xl bg-[#f0fbfb] border border-[#2ec9c9]/30 p-3 text-xs" data-testid="gigs-feed">
          <p className="text-[#16243b] font-medium mb-1">Your private calendar link (auto-updates):</p>
          <div className="flex gap-2">
            <input readOnly value={feed.feed_url} onFocus={(e) => e.target.select()} className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 outline-none" data-testid="gigs-feed-url" />
            <a href={feed.webcal_url} className="gp-btn-teal rounded-lg px-3 py-1.5 font-medium">Add now</a>
          </div>
        </div>
      )}
      {Object.entries(groups).map(([month, items]) => (
        <div key={month}>
          <h3 className="text-sm font-bold text-[#8a97a8] uppercase tracking-wide mb-3">{month}</h3>
          <div className="space-y-2">
            {items.map((g) => {
              const d = new Date(g.date);
              return (
                <div key={g.event_id} data-testid={`gig-${g.event_id}`} className="bg-white rounded-2xl p-4 shadow-[0_6px_20px_rgba(20,30,55,0.06)] flex items-center gap-4">
                  <div className="w-14 shrink-0 text-center">
                    <div className="text-2xl font-bold text-[#16243b] leading-none">{d.getDate()}</div>
                    <div className="text-[11px] uppercase text-[#8a97a8] font-semibold mt-0.5">{d.toLocaleString("en-GB", { weekday: "short" })}</div>
                  </div>
                  <div className="w-px self-stretch bg-gray-100" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#16243b] truncate">{g.title}</p>
                    <p className="text-xs text-[#8a97a8] mt-0.5">
                      {g.set_time ? `Set ${g.set_time}` : d.toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit" })} · with {g.with}{g.venue ? ` · ${g.venue}` : ""}
                    </p>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${g.role === "Lineup" ? "bg-[#2ec9c9]/10 text-[#1fb0b0]" : "bg-green-100 text-green-700"}`}>{g.role}</span>
                  <button onClick={() => downloadGigIcs(g)} data-testid={`gig-ics-${g.event_id}`} title="Add to calendar" className="text-[#8a97a8] hover:text-[#1fb0b0]"><CalendarDays size={16} /></button>
                  {g.store_slug && g.event_slug && (
                    <a href={`/store/${g.store_slug}/events/${g.event_slug}`} target="_blank" rel="noreferrer" data-testid={`gig-link-${g.event_id}`} className="text-[#8a97a8] hover:text-[#1fb0b0]"><ExternalLink size={16} /></a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function InboxTab() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/artist/enquiries").then(({ data }) => { setList(data); setOpen((o) => o ? data.find((e) => e.id === o.id) || null : null); });
  useEffect(() => { load(); }, []);
  const send = async (id, body) => { await api.post(`/artist/enquiries/${id}/message`, { body }); await load(); };
  const status = async (id, s) => { await api.post(`/artist/enquiries/${id}/status`, { status: s }); await load(); toast({ title: `Marked ${s.replace("_", " ")}` }); };
  if (!list) return <Loader2 className="animate-spin text-[#2ec9c9]" size={28} />;
  if (list.length === 0) return <div className="bg-white rounded-2xl p-8 text-center text-[#8a97a8]" data-testid="artist-inbox-empty">No enquiries yet. Head to <strong>Find &amp; enquire</strong> to reach out to venues and promoters.</div>;
  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4">
      <div className="space-y-2">
        {list.map((e) => (
          <button key={e.id} onClick={() => setOpen(e)} data-testid={`artist-enquiry-${e.id}`}
            className={`w-full text-left bg-white rounded-xl p-3 border transition-colors ${open?.id === e.id ? "border-[#2ec9c9]" : "border-transparent hover:border-gray-200"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-[#16243b] text-sm truncate">{e.party_name}</span>
              <StatusBadge status={e.status} />
            </div>
            <p className="text-xs text-[#8a97a8] truncate mt-0.5">{e.subject}</p>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        {open ? <EnquiryThread enquiry={open} role="artist" onSend={send} onStatus={status} /> : <p className="text-[#8a97a8] text-center py-10">Pick an enquiry to view the conversation.</p>}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/artist/bookings").then(({ data }) => setData(data)); }, []);
  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={28} />;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <h3 className="font-semibold text-[#16243b] mb-4 flex items-center gap-2"><PoundSterling size={18} className="text-[#2ec9c9]" /> Your pay deals</h3>
        {data.deals.length === 0 ? <p className="text-[#8a97a8] text-sm" data-testid="artist-deals-empty">No pay deals set up yet. Organisers you work with will add these.</p> : (
          <div className="space-y-2" data-testid="artist-deals-list">
            {data.deals.map((d, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-[#f6f8fb] p-3">
                <div>
                  <p className="font-medium text-[#16243b] text-sm">{d.business_name}{d.event_title ? ` · ${d.event_title}` : " · Default rate"}</p>
                  <p className="text-xs text-[#8a97a8]">{dealLabel(d)}{d.event_date ? ` · ${new Date(d.event_date).toLocaleDateString("en-GB")}` : ""}</p>
                </div>
                {d.expected_pay != null && (
                  <div className="text-right">
                    <p className="font-bold text-[#16a34a]">£{Number(d.expected_pay).toFixed(2)}</p>
                    <p className="text-[10px] text-[#8a97a8]">{d.tickets_sold} sold</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <h3 className="font-semibold text-[#16243b] mb-4">Confirmed bookings</h3>
        {data.bookings.length === 0 ? <p className="text-[#8a97a8] text-sm">No confirmed bookings yet.</p> : (
          <div className="space-y-2">
            {data.bookings.map((b, i) => (
              <div key={i} className="rounded-xl bg-[#f0fbfb] p-3">
                <p className="font-medium text-[#16243b] text-sm">{b.party_name} · {b.event_title || b.subject}</p>
                <p className="text-xs text-[#8a97a8]">{b.event_date ? new Date(b.event_date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : (b.proposed_date || "Date TBC")}{b.proposed_fee != null ? ` · £${Number(b.proposed_fee).toFixed(0)}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FindTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [enq, setEnq] = useState(null); // recipient to enquire
  const load = (query = "") => api.get(`/artist/directory?q=${encodeURIComponent(query)}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex gap-2 mb-4 max-w-[420px]">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} placeholder="Search venues & promoters…" data-testid="artist-directory-search"
          className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
        <button onClick={() => load(q)} className="gp-btn-teal rounded-xl px-4 py-2.5 text-sm font-medium">Search</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => (
          <div key={`${r.type}-${r.id}`} className="bg-white rounded-2xl p-4 shadow-[0_6px_20px_rgba(20,30,55,0.06)]" data-testid={`directory-${r.id}`}>
            <div className="flex items-center gap-2 mb-2">
              {r.type === "venue" ? <MapPin size={16} className="text-[#8b5cf6]" /> : <Building2 size={16} className="text-[#2ec9c9]" />}
              <span className="text-[10px] uppercase tracking-wide font-semibold text-[#8a97a8]">{r.type === "venue" ? "Venue" : "Promoter"}</span>
            </div>
            <p className="font-semibold text-[#16243b]">{r.name}</p>
            {r.city && <p className="text-xs text-[#8a97a8] mt-0.5">{r.city}</p>}
            <button onClick={() => setEnq(r)} data-testid={`enquire-${r.id}`} className="mt-3 w-full gp-btn-outline rounded-lg py-2 text-sm font-medium">Send enquiry</button>
          </div>
        ))}
      </div>
      {enq && <EnquireModal recipient={enq} onClose={() => setEnq(null)} />}
    </div>
  );
}

function EnquireModal({ recipient, onClose }) {
  const [f, setF] = useState({ subject: "", message: "", proposed_date: "", proposed_fee: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!f.message.trim()) { toast({ title: "Write a message", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/artist/enquiries", { to_type: recipient.type, to_id: recipient.id, subject: f.subject || "Booking enquiry", message: f.message, proposed_date: f.proposed_date, proposed_fee: f.proposed_fee === "" ? null : Number(f.proposed_fee) });
      toast({ title: "Enquiry sent", description: `Sent to ${recipient.name}` });
      onClose();
    } catch (e) { toast({ title: "Failed to send", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="enquire-modal">
      <div className="bg-white rounded-2xl w-full max-w-[460px] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-[#16243b]">Enquire · {recipient.name}</h3>
          <button onClick={onClose}><X className="text-[#5a6b80]" /></button>
        </div>
        <div className="space-y-3">
          <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject (e.g. Available for NYE)" data-testid="enquire-subject" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Introduce yourself and what you're proposing…" rows={4} data-testid="enquire-message" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={f.proposed_date} onChange={(e) => setF({ ...f, proposed_date: e.target.value })} data-testid="enquire-date" className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
            <input type="number" value={f.proposed_fee} onChange={(e) => setF({ ...f, proposed_fee: e.target.value })} placeholder="Fee £" data-testid="enquire-fee" className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          </div>
          <button onClick={send} disabled={busy} data-testid="enquire-send" className="gp-btn-teal w-full rounded-xl py-2.5 font-medium disabled:opacity-60">{busy ? "Sending…" : "Send enquiry"}</button>
        </div>
      </div>
    </div>
  );
}
